import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Container,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { theme, spacing, responsiveSpacing, responsiveFontSizes } from '../theme/theme';
// 移除 SEOHead 及结构化数据模板，不再注入复杂 SEO 头部

// Import feature images
import astrolabeImage from '../assets/images/紫微斗数排盘.png';
import aiConsultationImage from '../assets/images/AI问答.png';
import reportsImage from '../assets/images/运势报告.png';
import soulmatePaintingImage from '../assets/images/真爱画像.png';

const FeatureCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  },
}));

const PricingCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
}));

const PopularBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: -8,
  right: 16,
  backgroundColor: theme.palette.secondary.main,
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.75rem',
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
}));

interface Feature {
  image: string;
  title: string;
}

interface PricingPlan {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  popular?: boolean;
  buttonText: string;
}

const FeaturesScreen: React.FC = () => {
  // 页面使用固定中文文案，不再依赖多语言上下文
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>('features');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const features: Feature[] = [
    { image: astrolabeImage, title: '紫微斗数排盘分析' },
    { image: reportsImage, title: '运势报告' },
    { image: soulmatePaintingImage, title: '真爱画像' },
    { image: aiConsultationImage, title: 'AI智能问答' },
  ];

  const pricingPlans: PricingPlan[] = [
    {
      id: 'soulmatepainting',
      name: '真爱画像',
      price: '$4.49',
      period: '一次性',
      description: 'AI生成专属真爱画像，找到命中注定的另一半',
      features: ['AI生成个性化画像', '个性化艺术作品', '高分辨率图像', '感情建议'],
      buttonText: '立即生成'
    },
    {
      id: 'reports',
      name: '运势报告订阅',
      price: '$4.49',
      period: '/月',
      description: '月度运势报告和详细分析（十年运势报告免费）',
      features: ['十年运势报告（免费）', '月度运势分析', '每日运势预测', '个性化洞察分析'],
      popular: true,
      buttonText: '立即订阅'
    },
    {
      id: 'consultation',
      name: 'AI问答咨询',
      price: '$4.99',
      period: '一次性',
      description: '免费咨询2次，付费解锁无限AI智能问答咨询服务',
      features: ['前2次免费体验', '付费解锁无限AI问答'],
      buttonText: '立即购买'
    }
  ];

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold', 
              color: theme.colors.primary, 
              mb: 2,
              fontSize: responsiveFontSizes.h3
            }}
          >
            功能介绍
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: theme.colors.text, 
              mb: 4,
              fontSize: responsiveFontSizes.h6
            }}
          >
            探索 紫微AI 的强大功能
          </Typography>
        </Box>

        <Accordion expanded={expanded === 'features'} onChange={handleChange('features')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.primary }}>
              🌟 核心功能
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: {
                      xs: 300,
                      sm: 400,
                      md: 500
                    },
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      padding: 3,
                      display: 'flex',
                      alignItems: 'flex-end'
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                      }}
                    >
                      {feature.title}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'pricing'} onChange={handleChange('pricing')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.primary }}>
              💰 价格方案
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: theme.colors.text, 
                  mb: 2 
                }}
              >
                选择适合您的服务方案
              </Typography>
            </Box>
            <Grid container spacing={3}>
              {pricingPlans.map((plan, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Box sx={{ position: 'relative' }}>
                    {plan.popular && (
                      <PopularBadge label={'最受欢迎'} />
                    )}
                    <PricingCard>
                      <CardContent sx={{ p: 3, flexGrow: 1 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 'bold', 
                            color: theme.colors.primary, 
                            mb: 1,
                            textAlign: 'center'
                          }}
                        >
                          {plan.name}
                        </Typography>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 'bold', 
                              color: theme.colors.accent 
                            }}
                          >
                            {plan.price}
                            <Typography 
                              component="span" 
                              variant="body2" 
                              sx={{ color: theme.colors.text }}
                            >
                              {plan.period}
                            </Typography>
                          </Typography>
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.colors.text, 
                            textAlign: 'center', 
                            mb: 3 
                          }}
                        >
                          {plan.description}
                        </Typography>
                        <List dense>
                          {plan.features.map((feature, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                              <StarIcon sx={{ color: theme.colors.accent, mr: 1, fontSize: 16 }} />
                              <ListItemText 
                                primary={feature} 
                                sx={{ 
                                  '& .MuiListItemText-primary': { 
                                    fontSize: '0.85rem',
                                    color: theme.colors.text
                                  } 
                                }} 
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                      <Box sx={{ p: 3, pt: 0 }}>
                        <Button
                          variant={plan.popular ? 'contained' : 'outlined'}
                          fullWidth
                          onClick={() => { /* 订阅入口已移除，此按钮暂不跳转 */ }}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 'bold',
                            ...(plan.popular ? {
                              backgroundColor: theme.colors.accent,
                              '&:hover': {
                                backgroundColor: theme.colors.accent,
                              }
                            } : {
                              borderColor: theme.colors.primary,
                              color: theme.colors.primary,
                              '&:hover': {
                                backgroundColor: theme.colors.primary,
                                color: 'white',
                              }
                            })
                          }}
                        >
                          {plan.buttonText}
                        </Button>
                      </Box>
                    </PricingCard>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'security'} onChange={handleChange('security')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.colors.primary }}>
              🔒 安全与隐私
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SectionCard>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <SecurityIcon sx={{ fontSize: 48, color: theme.colors.primary, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      数据隐私
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text }}>
                      采用银行级别的数据加密技术，确保您的个人信息安全
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <SecurityIcon sx={{ fontSize: 48, color: theme.colors.accent, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      隐私保护
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text }}>
                      严格遵守隐私保护法规，绝不泄露您的个人信息
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <SecurityIcon sx={{ fontSize: 48, color: theme.colors.accent, mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      安全支付
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text }}>
                      支持多种安全支付方式，交易过程全程加密保护
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </SectionCard>
          </AccordionDetails>
        </Accordion>
      </Container>
    </>
  );
};

export default FeaturesScreen;